/**
 * Request microphone permission before starting speech-to-text.
 * Mirrors the permission flow used in the mobile app (speech_to_text_widget.dart):
 * request permission first, then only proceed if granted.
 */

export type RequestMicResult = { granted: true } | { granted: false; errorMessage: string };

/**
 * Requests microphone permission via getUserMedia. Stops the stream immediately
 * after—we only need to trigger the browser prompt and confirm access.
 * If already granted, this resolves quickly without a prompt.
 */
export async function requestMicPermission(): Promise<RequestMicResult> {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return {
      granted: false,
      errorMessage: "Microphone is not supported in this browser.",
    };
  }

  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((t) => t.stop());
    return { granted: true };
  } catch (err: unknown) {
    const e = err as { name?: string; message?: string };
    const name = e?.name || "";
    const msg = String(e?.message || "");

    if (name === "NotAllowedError" || /permission|denied/i.test(msg)) {
      return {
        granted: false,
        errorMessage:
          "Microphone permission is denied. Please enable microphone access from settings.",
      };
    }
    if (name === "NotFoundError") {
      return {
        granted: false,
        errorMessage: "No microphone found. Connect a mic and try again.",
      };
    }
    if (name === "NotReadableError" || name === "SecurityError") {
      return {
        granted: false,
        errorMessage:
          "Microphone is currently in use by another app. Please try again.",
      };
    }
    return {
      granted: false,
      errorMessage: "Unable to access the microphone. Please try again.",
    };
  }
}
